import { InitialAdminPasswordReset } from "@/components/InitialAdminPasswordReset";
import { useAuth } from "@/context/AuthContext";
import { SaveDialogContent, SaveButton, SaveForm } from "@/components/SaveForm";
import { useEffect, useState } from "react";
import api, { apiErr, tenantLogoUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Trash2, AlertTriangle, ImagePlus } from "lucide-react";

const emptyForm = {
  nome: "", nome_fantasia: "", cnpj: "",
  admin_name: "", admin_email: "", admin_password: "",
};
const inp = "bg-slate-950 border-slate-700 text-slate-100 mt-1";
const formatCnpj = (value) => {
  const d = String(value || "").replace(/\D/g, "");
  if (d.length !== 14) return value || "—";
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
};

export default function Companies() {
  const { user } = useAuth();
  const owner = user?.is_platform_owner === true;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCheck, setDeleteCheck] = useState(null);
  const [newLogo, setNewLogo] = useState(null);
  const [editLogo, setEditLogo] = useState(null);
  const [editRemoveLogo, setEditRemoveLogo] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/platform/tenants");
      setList(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.nome || !form.nome_fantasia || !form.admin_name || !form.admin_email || !form.admin_password) {
      toast.error("Preencha a empresa e o administrador inicial.");
      return;
    }
    setBusy(true);
    try {
      const { data: tenant } = await api.post("/platform/tenants", form);
      let logoOk = true;
      if (newLogo) {
        try {
          const logoData = new FormData();
          logoData.append("file", newLogo);
          await api.post(`/platform/tenants/${tenant.id}/logo`, logoData);
        } catch (logoError) {
          logoOk = false;
          toast.warning(`Empresa criada, mas o logo não foi salvo: ${apiErr(logoError)}`);
        }
      }
      if (logoOk) toast.success("Empresa criada com administrador inicial.");
      setOpen(false);
      setForm(emptyForm);
      setNewLogo(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openDelete = async (tenant) => {
    setDeleteTarget(tenant);
    setDeleteCheck(null);
    try {
      const { data } = await api.get(`/platform/tenants/${tenant.id}/can-delete`);
      setDeleteCheck(data);
    } catch (e) {
      toast.error(apiErr(e));
      setDeleteTarget(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !deleteCheck?.can_delete) return;
    setBusy(true);
    try {
      await api.delete(`/platform/tenants/${deleteTarget.id}`);
      toast.success("Empresa excluída definitivamente.");
      setDeleteTarget(null);
      setDeleteCheck(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/platform/tenants/${edit.id}`, {
        nome: edit.nome,
        nome_fantasia: edit.nome_fantasia,
        cnpj: edit.cnpj || "",
        ...(owner ? { ativo: edit.ativo } : {}),
      });
      let logoOk = true;
      try {
        if (editRemoveLogo) {
          await api.delete(`/platform/tenants/${edit.id}/logo`);
        } else if (editLogo) {
          const logoData = new FormData();
          logoData.append("file", editLogo);
          await api.post(`/platform/tenants/${edit.id}/logo`, logoData);
        }
      } catch (logoError) {
        logoOk = false;
        toast.warning(`Empresa atualizada, mas o logo não foi salvo: ${apiErr(logoError)}`);
      }
      if (logoOk) toast.success("Empresa atualizada.");
      setEdit(null);
      setEditLogo(null);
      setEditRemoveLogo(false);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-slate-400">Carregando empresas...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-50">Empresas</h1>
          <p className="text-sm text-slate-400">Clientes do Peça360. Cada empresa mantém seus dados isolados.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-violet-700 hover:bg-violet-600">
          <Plus className="h-4 w-4 mr-1" /> Nova empresa
        </Button>
      </div>

      <Card className="card-metal p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
                <th className="text-left px-4 py-3">Empresa</th>
                <th className="text-left px-4 py-3">CNPJ</th>
                <th className="text-center px-4 py-3">Usuários</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <tr key={t.id} className="border-t border-slate-800">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 text-slate-100">
                      {t.has_logo ? (
                        <img src={tenantLogoUrl(t.id)} alt={t.nome_fantasia} className="h-9 w-12 object-contain rounded bg-slate-950/50" />
                      ) : (
                        <div className="h-9 w-12 rounded bg-slate-950/50 border border-slate-800 flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-sky-400" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{t.nome_fantasia}</div>
                        <div className="text-xs text-slate-500">{t.nome}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{formatCnpj(t.cnpj)}</td>
                  <td className="px-4 py-3 text-center text-slate-300">{t.total_usuarios ?? 0}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={t.ativo !== false ? "text-emerald-400" : "text-rose-400"}>
                      {t.ativo !== false ? "Ativa" : "Inativa"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => { setEdit({ ...t }); setEditLogo(null); setEditRemoveLogo(false); }}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                    </Button>
                    {owner && <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => openDelete(t)}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Excluir
                    </Button>}
                  </td>
                </tr>
              ))}
              {!list.length && (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">Nenhuma empresa cadastrada.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setNewLogo(null); }}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <SaveForm disabled={busy} onSave={create} className="space-y-4">
            <DialogHeader><DialogTitle className="font-display text-2xl">Nova empresa</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Razão social *</Label><Input className={inp} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
            <div><Label>Nome fantasia *</Label><Input className={inp} value={form.nome_fantasia} onChange={(e) => setForm({ ...form, nome_fantasia: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>CNPJ</Label><Input className={inp} value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} /></div>
            <div className="sm:col-span-2">
              <Label>Logo da empresa</Label>
              <div className="mt-1 rounded-lg border border-dashed border-slate-700 bg-slate-950/50 p-3">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-300">
                  <ImagePlus className="h-4 w-4 text-sky-400" />
                  <span>{newLogo ? newLogo.name : "Selecionar logo (PNG, JPG ou WEBP, até 2 MB)"}</span>
                  <input type="file" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setNewLogo(e.target.files?.[0] || null)} />
                </label>
              </div>
            </div>
            <div className="sm:col-span-2 border-t border-slate-800 pt-3 mt-1 text-sm font-medium text-sky-300">Administrador inicial</div>
            <div><Label>Nome *</Label><Input className={inp} value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} /></div>
            <div><Label>E-mail *</Label><Input type="email" className={inp} value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Senha inicial *</Label><Input type="password" className={inp} value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} /></div>
          </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
              <SaveButton type="submit" disabled={busy} className="bg-violet-700 hover:bg-violet-600">{busy ? "Criando..." : "Criar empresa"}</SaveButton>
            </DialogFooter>
          </SaveForm>
        </SaveDialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(v) => { if (!v && !resetBusy) { setEdit(null); setEditLogo(null); setEditRemoveLogo(false); } }}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <SaveForm disabled={busy || resetBusy} onSave={saveEdit} className="space-y-4">
            <DialogHeader><DialogTitle className="font-display text-2xl">Editar empresa</DialogTitle></DialogHeader>
          {edit && <div className="space-y-3">
            <div><Label>Razão social</Label><Input className={inp} value={edit.nome || ""} onChange={(e) => setEdit({ ...edit, nome: e.target.value })} /></div>
            <div><Label>Nome fantasia</Label><Input className={inp} value={edit.nome_fantasia || ""} onChange={(e) => setEdit({ ...edit, nome_fantasia: e.target.value })} /></div>
            <div><Label>CNPJ</Label><Input className={inp} value={edit.cnpj || ""} onChange={(e) => setEdit({ ...edit, cnpj: e.target.value })} /></div>
            <div>
              <Label>Logo da empresa</Label>
              <div className="mt-1 rounded-lg border border-slate-700 bg-slate-950/50 p-3 space-y-3">
                {edit.has_logo && !editRemoveLogo && (
                  <div className="flex items-center gap-3">
                    <img src={tenantLogoUrl(edit.id)} alt={edit.nome_fantasia} className="h-12 max-w-28 object-contain" />
                    {owner && <Button type="button" size="sm" variant="ghost" className="text-rose-400" onClick={() => { setEditRemoveLogo(true); setEditLogo(null); }}>
                      Remover logo
                    </Button>}
                  </div>
                )}
                {(!edit.has_logo || editRemoveLogo) && !editLogo && (
                  <div className="text-xs text-slate-500">Sem logo cadastrado.</div>
                )}
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-300">
                  <ImagePlus className="h-4 w-4 text-sky-400" />
                  <span>{editLogo ? editLogo.name : edit.has_logo && !editRemoveLogo ? "Substituir logo" : "Selecionar logo"}</span>
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => { setEditLogo(e.target.files?.[0] || null); if (e.target.files?.[0]) setEditRemoveLogo(false); }}
                  />
                </label>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-950/60 px-3 py-3">
              <Label>Empresa ativa</Label>
              <Switch disabled={!owner} checked={edit.ativo !== false} onCheckedChange={(v) => setEdit({ ...edit, ativo: v })} />
            </div>
          </div>}
            <DialogFooter>
              <Button type="button" variant="ghost" disabled={resetBusy} onClick={() => setEdit(null)}>Cancelar</Button>
              <SaveButton type="submit" disabled={busy || resetBusy} className="bg-violet-700 hover:bg-violet-600">{busy ? "Salvando..." : "Salvar"}</SaveButton>
            </DialogFooter>
          </SaveForm>
          {owner && edit && <InitialAdminPasswordReset key={edit.id} tenantId={edit.id} onBusyChange={setResetBusy} />}
        </SaveDialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setDeleteCheck(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-rose-400" /> Excluir empresa
            </DialogTitle>
          </DialogHeader>

          {!deleteCheck ? (
            <p className="text-sm text-slate-400">Verificando dados da empresa...</p>
          ) : deleteCheck.can_delete ? (
            <div className="rounded-lg border border-rose-800/60 bg-rose-950/30 p-4 space-y-2">
              <div className="flex items-center gap-2 text-rose-300 font-semibold">
                <AlertTriangle className="h-5 w-5" /> Exclusão definitiva
              </div>
              <p className="text-sm text-slate-300">
                A empresa <b className="text-slate-100">{deleteTarget?.nome_fantasia}</b> não possui dados operacionais.
              </p>
              <p className="text-sm text-rose-200">
                A empresa e seus usuários serão apagados. Esta operação não poderá ser desfeita.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-800/60 bg-amber-950/30 p-4 space-y-2">
              <div className="flex items-center gap-2 text-amber-300 font-semibold">
                <AlertTriangle className="h-5 w-5" /> Exclusão bloqueada
              </div>
              <p className="text-sm text-slate-300">{deleteCheck.reason}</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDeleteTarget(null); setDeleteCheck(null); }}>Cancelar</Button>
            {deleteCheck?.can_delete && (
              <Button disabled={busy} onClick={confirmDelete} className="bg-rose-700 hover:bg-rose-600">
                {busy ? "Excluindo..." : "Excluir definitivamente"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
